import { resolveVehicleCollision } from "./collision.js";
import { getVehicleZoneOccupancy, getWheelContacts, } from "./vehicleGeometry.js";
import { applyTireWearDetailed, normalizeTireHealth, tireGripMultiplier, } from "./tireWear.js";
import { FRESH_TIRE_HEALTH, TerrainType } from "./types.js";
import { PHYSICS_CONFIG } from "./physicsConfig.js";
import { quantizeVehicleState } from "./version.js";
const REST_SPEED = 0.2;
/**
 * Number of fixed sub-steps the dynamic (vehicle) integrator runs per simulation
 * tick. The lateral/yaw tire model is a stiff ODE: at a full render-tick step
 * (dt = 1) explicit forward-Euler integration of the tire damping is unstable
 * and makes the car spin without bound. Sub-stepping shrinks the effective
 * integration step so the same tuned forces integrate stably.
 */
const DYNAMIC_SUBSTEPS = 12;
/**
 * Hard guard on body yaw rate (rad per tick). A steady tight corner needs well
 * under 0.1 rad/tick, so this never interferes with real driving; it only caps
 * a numerical excursion so a transient can never present as an endless spin.
 */
const MAX_YAW_RATE = 1;
export function stepVehicle(input) {
    const simulationStepMs = Number.isFinite(input.stepMs)
        ? Math.max(input.stepMs, 0)
        : 0;
    const dt = simulationStepMs / (1000 / 60);
    const stateWithTires = {
        ...normalizeNumericState(input.state),
        tires: normalizeTireHealth(input.state.tires),
    };
    const simulationInput = {
        ...input,
        state: stateWithTires,
        stepMs: simulationStepMs,
    };
    const next = stepCar(simulationInput, dt);
    const tireWear = applyTireWearDetailed(next.state.tires, getWheelContacts(stateWithTires, input.car, input.sampler), {
        ...next.telemetry,
        corneringUtilization: {
            frontLeft: next.telemetry.frontSaturationRatio,
            frontRight: next.telemetry.frontSaturationRatio,
            rearLeft: next.telemetry.rearSaturationRatio,
            rearRight: next.telemetry.rearSaturationRatio,
        },
    }, input.car, simulationStepMs);
    const wornState = { ...next.state, tires: tireWear.tires };
    const collision = resolveVehicleCollision(stateWithTires, wornState, input.car, input.sampler, input.tick);
    const pit = applyPitEffects(collision.state, input, input.state.pitServiceContact ?? false);
    const state = quantizeVehicleState(pit.state);
    const maxSpeed = input.car.maxSpeed * input.track.trackMaxSpeed;
    const telemetry = {
        ...next.telemetry,
        speed: Math.hypot(state.longitudinalSpeed, state.lateralSpeed),
        speedRatio: Math.min(1, Math.hypot(state.longitudinalSpeed, state.lateralSpeed) /
            Math.max(maxSpeed, 0.001)),
        lateralSpeed: state.lateralSpeed,
        yawRate: state.yawRate,
        steeringAngleRad: state.steeringAngleRad,
        tireWear: tireWear.telemetry,
        pitServiceActive: pit.pitServiceActive,
        pitSpeedLimitActive: pit.pitSpeedLimitActive,
        pitServiceVisit: pit.pitServiceVisit,
        pitStopVisitCount: state.pitStopVisitCount ?? 0,
        pitLimiterApplied: pit.pitLimiterApplied,
        wallHit: collision.wallHit
            ? { ...collision.wallHit, impulse: quantize(collision.wallHit.impulse) }
            : undefined,
    };
    return { state, telemetry };
}
function normalizeNumericState(state) {
    const finite = (value) => Number.isFinite(value) ? value : 0;
    return {
        ...state,
        position: { x: finite(state.position.x), y: finite(state.position.y) },
        headingRad: finite(state.headingRad),
        longitudinalSpeed: finite(state.longitudinalSpeed),
        lateralSpeed: finite(state.lateralSpeed),
        yawRate: finite(state.yawRate),
        steeringAngleRad: finite(state.steeringAngleRad),
        pitServiceContact: state.pitServiceContact ?? false,
        pitStopVisitCount: Math.max(0, Math.min(255, Math.trunc(state.pitStopVisitCount ?? 0))),
    };
}
/** Apply calibrated zone effects after the wall solver resolves. */
function applyPitEffects(resolvedState, input, previousServiceContact) {
    const occupancy = getVehicleZoneOccupancy(resolvedState, input.car, input.sampler);
    const pitServiceVisit = occupancy.pitServiceActive && !previousServiceContact;
    const visitCount = Math.min(255, Math.max(0, Math.trunc(input.state.pitStopVisitCount ?? 0)) +
        (pitServiceVisit ? 1 : 0));
    let longitudinalSpeed = resolvedState.longitudinalSpeed;
    let pitLimiterApplied = false;
    if (occupancy.pitSpeedLimitActive) {
        const normalMaxSpeed = applicableMaxSpeed(resolvedState, input.car, input.track, input.sampler);
        const ratio = clamp(input.track.pitSpeedLimitRatio ?? 0.5, 0, 1);
        const forwardCap = normalMaxSpeed * ratio;
        const reverseCap = normalMaxSpeed * Math.min(0.5, ratio);
        const cap = longitudinalSpeed < 0 ? reverseCap : forwardCap;
        const excess = Math.abs(longitudinalSpeed) - cap;
        if (excess > 0) {
            const deceleration = Math.max(0, Number.isFinite(input.track.pitLimiterDeceleration ?? 0)
                ? (input.track.pitLimiterDeceleration ?? 0)
                : 0);
            const dt = Number.isFinite(input.stepMs)
                ? Math.max(0, input.stepMs / (1000 / 60))
                : 0;
            longitudinalSpeed =
                Math.sign(longitudinalSpeed) *
                    Math.max(cap, Math.abs(longitudinalSpeed) - deceleration * dt);
            pitLimiterApplied = true;
        }
    }
    let state = {
        ...resolvedState,
        longitudinalSpeed,
        pitServiceContact: occupancy.pitServiceActive,
        pitStopVisitCount: visitCount,
    };
    if (pitServiceVisit)
        state = { ...state, tires: { ...FRESH_TIRE_HEALTH } };
    return { state, ...occupancy, pitServiceVisit, pitLimiterApplied };
}
function applicableMaxSpeed(state, car, track, sampler) {
    const contacts = getWheelContacts(state, car, sampler);
    if (contacts.length === 0)
        return Math.max(0, car.maxSpeed * track.trackMaxSpeed);
    let weighted = 0;
    let total = 0;
    for (const contact of contacts) {
        const modifier = terrainModifier(contact.terrain, track, car);
        const weight = Math.max(0, contact.normalLoadRatio);
        weighted += modifier.maxSpeed * weight;
        total += weight;
    }
    return Math.max(0, car.maxSpeed * (total > 0 ? weighted / total : track.trackMaxSpeed));
}
function stepCar(input, dt) {
    const { state, car, controls, track } = input;
    const contacts = getWheelContacts(state, car, input.sampler);
    let frontGrip = 0;
    let rearGrip = 0;
    let traction = 0;
    let braking = 0;
    let terrainDrag = 0;
    let maxSpeed = 0;
    const brakeGripByWheel = new Map();
    const frontLoadTotal = contacts
        .filter((contact) => contact.wheel === "frontLeft" || contact.wheel === "frontRight")
        .reduce((total, contact) => total + contact.normalLoadRatio, 0);
    const rearLoadTotal = contacts
        .filter((contact) => contact.wheel === "rearLeft" || contact.wheel === "rearRight")
        .reduce((total, contact) => total + contact.normalLoadRatio, 0);
    for (const contact of contacts) {
        const modifier = terrainModifier(contact.terrain, track, car);
        const tireGrip = tireGripMultiplier(state.tires?.[contact.wheel] ?? 100, car);
        const powerGrip = contact.terrain === TerrainType.Grass &&
            (contact.wheel === "rearLeft" || contact.wheel === "rearRight")
            ? 1 -
                clamp(car.grassPowerGripLoss ?? PHYSICS_CONFIG.grassPowerGripLoss, 0, 1) *
                    Math.abs(controls.throttle)
            : 1;
        const weightedGrip = modifier.grip * tireGrip * powerGrip * contact.normalLoadRatio;
        if (contact.wheel === "frontLeft" || contact.wheel === "frontRight")
            frontGrip += weightedGrip;
        else
            rearGrip += weightedGrip;
        traction += modifier.traction * contact.normalLoadRatio;
        if (contact.wheel === "rearLeft" || contact.wheel === "rearRight") {
            brakeGripByWheel.set(contact.wheel, modifier.braking *
                tireGrip *
                (contact.normalLoadRatio / Math.max(rearLoadTotal, 0.001)));
        }
        terrainDrag += modifier.drag * contact.normalLoadRatio;
        maxSpeed += modifier.maxSpeed * contact.normalLoadRatio;
    }
    frontGrip /= Math.max(frontLoadTotal, 0.001);
    rearGrip /= Math.max(rearLoadTotal, 0.001);
    braking =
        (brakeGripByWheel.get("rearLeft") ?? 0) +
            (brakeGripByWheel.get("rearRight") ?? 0);
    rearGrip = Math.min(rearGrip, lerp(1, frontGrip, car.tireSlideGripLoss ?? PHYSICS_CONFIG.tireSlideGripLoss));
    maxSpeed = maxSpeed * car.maxSpeed;
    // Steering is a rate-limited actuator, not part of the stiff subsystem, so it
    // is advanced once per tick. Its authority falls off with speed
    // (steeringSpeedSensitivity): full angle at a standstill for quick low-speed
    // cornering, reduced angle near top speed to tame high-speed oversteer.
    const referenceSpeed = Math.max(car.maxSpeed * track.trackMaxSpeed, 0.001);
    const speedFraction = clamp(Math.abs(state.longitudinalSpeed) / referenceSpeed, 0, 1);
    const steeringAuthority = 1 - clamp(car.steeringSpeedSensitivity, 0, 1) * speedFraction;
    const frontHealthResponse = axleHealthResponse(state.tires, "front", contacts);
    const rearHealthResponse = axleHealthResponse(state.tires, "rear", contacts);
    const effectiveMaxSteering = car.maximumSteeringAngle *
        steeringAuthority *
        (1 - clamp(car.tireSteeringLoss ?? 0, 0, 1) * (1 - frontHealthResponse)) *
        (1 -
            clamp(car.asymmetricCorneringLoss ?? PHYSICS_CONFIG.asymmetricCorneringLoss, 0, 1) *
                (controls.steering < 0
                    ? Math.max(0, (state.tires?.frontRight ?? 100) -
                        (state.tires?.frontLeft ?? 100)) / 100
                    : controls.steering > 0
                        ? Math.max(0, (state.tires?.frontLeft ?? 100) -
                            (state.tires?.frontRight ?? 100)) / 100
                        : 0));
    const steeringTarget = clamp(controls.steering * effectiveMaxSteering, -effectiveMaxSteering, effectiveMaxSteering);
    const currentSteering = clamp(state.steeringAngleRad, -effectiveMaxSteering, effectiveMaxSteering);
    const steeringDelta = steeringTarget - currentSteering;
    const effectiveSteeringResponse = car.steeringResponseRate *
        (1 - clamp(car.tireSteeringLoss ?? 0, 0, 1) * (1 - frontHealthResponse));
    const steering = currentSteering +
        clamp(steeringDelta, -effectiveSteeringResponse * dt, effectiveSteeringResponse * dt);
    const engine = controls.throttle *
        car.engineForce *
        (controls.throttle < 0 ? car.reverseForceRatio : 1) *
        traction;
    const brakeRequested = car.brakeForce * controls.brake * braking;
    // The lateral/yaw tire dynamics are stiff; integrate them with explicit
    // forward Euler over several small sub-steps so the tuned forces stay stable
    // at a full render tick. Terrain, grip and steering are held fixed across the
    // sub-steps (they change negligibly within one tick).
    const subDt = dt / DYNAMIC_SUBSTEPS;
    let longitudinal = state.longitudinalSpeed;
    let lateral = state.lateralSpeed;
    let yawRate = state.yawRate;
    let heading = state.headingRad;
    let positionX = state.position.x;
    let positionY = state.position.y;
    let appliedBrakeImpulse = 0;
    let requestedBrakeImpulse = 0;
    let frontSlipTotal = 0;
    let rearSlipTotal = 0;
    let frontSaturationTotal = 0;
    let rearSaturationTotal = 0;
    const cosSteering = Math.cos(steering);
    for (let sub = 0; sub < DYNAMIC_SUBSTEPS; sub += 1) {
        const speedSign = longitudinal < 0 ? -1 : 1;
        const safeSpeed = Math.max(Math.abs(longitudinal), 0.25);
        const frontSlip = Math.atan2(lateral + car.comToFrontAxle * yawRate, safeSpeed) -
            steering * speedSign;
        const rearSlip = Math.atan2(lateral - car.comToRearAxle * yawRate, safeSpeed);
        const frontForce = clamp(-car.frontCorneringStiffness *
            frontSlip *
            lerp(1, frontGrip, car.tireCorneringStiffnessLoss ??
                PHYSICS_CONFIG.tireCorneringStiffnessLoss), -car.maximumTireForce * frontGrip, car.maximumTireForce * frontGrip);
        const rearForce = clamp(-car.rearCorneringStiffness *
            rearSlip *
            lerp(1, rearGrip, car.tireCorneringStiffnessLoss ??
                PHYSICS_CONFIG.tireCorneringStiffnessLoss), -car.maximumTireForce * rearGrip, car.maximumTireForce * rearGrip);
        frontSlipTotal += frontSlip;
        rearSlipTotal += rearSlip;
        frontSaturationTotal +=
            Math.abs(frontForce) / Math.max(car.maximumTireForce * frontGrip, 0.001);
        rearSaturationTotal +=
            Math.abs(rearForce) / Math.max(car.maximumTireForce * rearGrip, 0.001);
        const brakeMagnitude = Math.min(Math.abs(longitudinal) / Math.max(subDt, 0.0001), brakeRequested);
        const brakeForce = brakeMagnitude * (longitudinal === 0 ? 0 : Math.sign(longitudinal));
        appliedBrakeImpulse += brakeMagnitude * subDt;
        requestedBrakeImpulse += brakeRequested * subDt;
        const rolling = (car.rollingDrag + terrainDrag) * longitudinal;
        const aero = car.aerodynamicDrag * longitudinal * Math.abs(longitudinal);
        const netDrive = engine - rolling - aero;
        // Scale only surplus propulsion, including its balancing road resistance.
        // The zero of netDrive (terminal speed) is independent of rear health.
        const accelerationRetention = controls.throttle !== 0 &&
            netDrive * controls.throttle > 0 &&
            longitudinal * controls.throttle >= 0
            ? lerp(1 -
                clamp(car.tireTractionLoss ?? PHYSICS_CONFIG.tireTractionLoss, 0, 1), 1, rearHealthResponse)
            : 1;
        const longitudinalAcceleration = (netDrive * accelerationRetention - brakeForce) / car.mass +
            yawRate * lateral;
        const lateralAcceleration = (frontForce * cosSteering + rearForce) / car.mass -
            yawRate * longitudinal;
        const yawAcceleration = (car.comToFrontAxle * frontForce * cosSteering -
            car.comToRearAxle * rearForce) /
            car.yawInertia;
        const previousLongitudinal = longitudinal;
        longitudinal += longitudinalAcceleration * subDt;
        lateral += lateralAcceleration * subDt;
        let dynamicYaw = yawRate + yawAcceleration * subDt;
        if (controls.brake !== 0 &&
            Math.sign(previousLongitudinal) !== 0 &&
            Math.sign(longitudinal) !== Math.sign(previousLongitudinal))
            longitudinal = 0;
        if (controls.throttle === 0 &&
            controls.brake === 0 &&
            Math.abs(longitudinal) < REST_SPEED)
            longitudinal = 0;
        longitudinal = clamp(longitudinal, -maxSpeed * 0.5, maxSpeed);
        // Blend to the stable kinematic bicycle at low speed, where the dynamic
        // model divides by a near-zero speed and would otherwise be unstable.
        const blend = smoothstep(car.lowSpeedBlendStart, car.lowSpeedBlendEnd, Math.abs(longitudinal));
        const kinematicYaw = (longitudinal * Math.tan(steering)) / Math.max(car.wheelbase, 0.001);
        yawRate = clamp(kinematicYaw * (1 - blend) + dynamicYaw * blend, -MAX_YAW_RATE, MAX_YAW_RATE);
        lateral *= Math.pow(blend, subDt);
        const midHeading = normalizeAngle(heading + yawRate * subDt * 0.5);
        const basis = bodyBasis(midHeading);
        positionX +=
            (basis.forwardX * longitudinal + basis.rightX * lateral) * subDt;
        positionY +=
            (basis.forwardY * longitudinal + basis.rightY * lateral) * subDt;
        heading = normalizeAngle(heading + yawRate * subDt);
    }
    const nextState = {
        position: {
            x: positionX,
            y: positionY,
        },
        headingRad: heading,
        longitudinalSpeed: longitudinal,
        lateralSpeed: lateral,
        yawRate,
        steeringAngleRad: steering,
        ...(state.tires ? { tires: normalizeTireHealth(state.tires) } : {}),
    };
    return {
        state: nextState,
        telemetry: {
            tick: input.tick,
            speed: Math.hypot(longitudinal, lateral),
            speedRatio: 0,
            lateralSpeed: lateral,
            yawRate,
            steeringAngleRad: steering,
            appliedThrottle: controls.throttle,
            appliedBrake: requestedBrakeImpulse > 0
                ? Math.min(1, appliedBrakeImpulse / requestedBrakeImpulse)
                : 0,
            appliedSteering: controls.steering,
            frontGripMultiplier: clamp(frontGrip, 0, 1),
            rearGripMultiplier: clamp(rearGrip, 0, 1),
            frontSlipAngle: frontSlipTotal / DYNAMIC_SUBSTEPS,
            rearSlipAngle: rearSlipTotal / DYNAMIC_SUBSTEPS,
            frontSaturationRatio: clamp(frontSaturationTotal / DYNAMIC_SUBSTEPS, 0, 1),
            rearSaturationRatio: clamp(rearSaturationTotal / DYNAMIC_SUBSTEPS, 0, 1),
        },
    };
}
function axleHealthResponse(tires, axle, contacts) {
    const left = axle === "front" ? "frontLeft" : "rearLeft";
    const right = axle === "front" ? "frontRight" : "rearRight";
    let total = 0;
    let load = 0;
    for (const contact of contacts) {
        if (contact.wheel !== left && contact.wheel !== right)
            continue;
        const health = tires?.[contact.wheel] ?? 100;
        const weight = Number.isFinite(contact.normalLoadRatio)
            ? Math.max(contact.normalLoadRatio, 0)
            : 0;
        total += clamp(health / 100, 0, 1) * weight;
        load += weight;
    }
    return load > 0 ? clamp(total / load, 0, 1) : 1;
}
function terrainModifier(terrain, track, car) {
    if (terrain === "grass")
        return {
            drag: track.drag,
            maxSpeed: track.trackMaxSpeed,
            grip: clamp(car.grassLateralGrip ?? PHYSICS_CONFIG.grassLateralGrip, 0.001, 1),
            traction: 1,
            braking: 1,
        };
    if (terrain === "gravel")
        return {
            drag: track.gravelDrag,
            maxSpeed: track.gravelMaxSpeed,
            grip: 0.55,
            traction: 0.55,
            braking: 0.65,
        };
    if (terrain === "wall" || terrain === "unknown")
        return { drag: 0, maxSpeed: 0, grip: 0, traction: 0, braking: 0 };
    return {
        drag: track.drag,
        maxSpeed: track.trackMaxSpeed,
        grip: 1,
        traction: 1,
        braking: 1,
    };
}
function bodyBasis(headingRad) {
    return {
        forwardX: Math.sin(headingRad),
        forwardY: -Math.cos(headingRad),
        rightX: Math.cos(headingRad),
        rightY: Math.sin(headingRad),
    };
}
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
function smoothstep(start, end, value) {
    return end <= start ? 1 : clamp((value - start) / (end - start), 0, 1);
}
function lerp(start, end, amount) {
    return start + (end - start) * clamp(amount, 0, 1);
}
function normalizeAngle(angle) {
    let normalized = angle;
    while (normalized > Math.PI)
        normalized -= Math.PI * 2;
    while (normalized < -Math.PI)
        normalized += Math.PI * 2;
    return normalized;
}
function quantize(value, decimals = 4) {
    const scale = 10 ** decimals;
    return Math.round(value * scale) / scale;
}
